import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useUser } from '../../contexts/UserContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { useAppSettings } from '../../hooks/useGlobalSettings';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const { resetPassword } = useUser();
  const { isDarkMode } = useCustomTheme();
  const { appName, appSubtitle, platformTagline, appDescription } = useAppSettings();
  useDocumentTitle('Reset Password');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setSuccess(true);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 2
      }}
    >
      <Grid container maxWidth="lg" spacing={4}>
        {/* Left side - Logo and branding */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center'
            }}
          >
            <Box
              component="img"
              src={isDarkMode ? "/Merit4all logo white.png" : "/Merit4all logo.png"}
              alt="Merit4all Logo"
              sx={{
                width: 240,
                height: 'auto',
                mb: 3,
                maxWidth: '100%'
              }}
            />
            <Typography variant="h3" component="h1" gutterBottom>
              {appName}
            </Typography>
            <Typography variant="h5" color="primary" sx={{ mb: 1, fontWeight: 500 }}>
              {appSubtitle}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {platformTagline}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
              {appDescription}
            </Typography>
          </Box>
        </Grid>

        {/* Right side - Password reset form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 400,
              mx: 'auto',
              borderRadius: 2
            }}
          >
            <Typography variant="h4" component="h2" gutterBottom align="center">
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              {success
                ? 'Check your email for reset instructions'
                : 'Enter your email address and we\'ll send you a password reset link'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Password reset email sent successfully! Please check your inbox and follow
                  the instructions to reset your password.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Didn't receive the email? Check your spam folder or try again with a different email address.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  sx={{ mb: 2 }}
                >
                  Try Another Email
                </Button>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to Login
                  </Button>
                </Link>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mb: 2, py: 1.5 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                </Button>

                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button
                    fullWidth
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to Login
                  </Button>
                </Link>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center'
        }}
      >
        <Typography variant="caption" color="text.disabled">
          Developed by Merit4all, all rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
